import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { authToken } from '../../scripts/authentication.script';

const baseUrl = (id_company: string) =>
  `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payroll`;

function authConfig() {
  return { headers: { Authorization: authToken() } };
}

async function request(fn: () => Promise<any>): Promise<ApiResponse> {
  let apiResponse: ApiResponse;
  try {
    const response = await fn();
    apiResponse = {
      status: response.status,
      message: response.statusText,
      data: response.data?.data ?? response.data,
    };
  } catch (error: any) {
    apiResponse = {
      status: error.response?.status ?? 500,
      message: error.response?.statusText ?? 'Error',
      data: error.response?.data?.error ?? error.response?.data ?? { code: 'NETWORK_ERROR', message: error.message },
    };
  }
  return apiResponse;
}

export class PayrollRunDA {
  // List payroll runs
  public static getPayrollRuns(
    id_company: string,
    query?: { year?: number; status?: string; page?: number; limit?: number }
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (query?.year) params.set('year', String(query.year));
    if (query?.status) params.set('status', query.status);
    if (query?.page) params.set('page', String(query.page));
    if (query?.limit) params.set('limit', String(query.limit));
    const qs = params.toString();
    const url = `${baseUrl(id_company)}/payroll-runs${qs ? `?${qs}` : ''}`;
    return request(() => axios.get(url, authConfig()));
  }

  // Create payroll run
  public static createPayrollRun(
    id_company: string,
    body: { year: number; month: number }
  ): Promise<ApiResponse> {
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs`, body, authConfig()));
  }

  // Get single payroll run
  public static getPayrollRun(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.get(`${baseUrl(id_company)}/payroll-runs/${id_run}`, authConfig()));
  }

  // Calculate
  public static calculate(
    id_company: string,
    id_run: string,
    company_snapshot?: {
      name: string;
      register_number: string;
      address: { street: string; number: string; additional: string; zip_code: string; city: string; country: string };
    }
  ): Promise<ApiResponse> {
    const body = company_snapshot ? { company_snapshot } : {};
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs/${id_run}/calculate`, body, authConfig()));
  }

  // Validate
  public static validate(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs/${id_run}/validate`, {}, authConfig()));
  }

  // Mark paid
  public static pay(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs/${id_run}/pay`, {}, authConfig()));
  }

  // Cancel
  public static cancel(
    id_company: string,
    id_run: string,
    body: { reason: string }
  ): Promise<ApiResponse> {
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs/${id_run}/cancel`, body, authConfig()));
  }

  // Get payslips for a run
  public static getPayslips(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.get(`${baseUrl(id_company)}/payroll-runs/${id_run}/payslips`, authConfig()));
  }

  // Download summary PDF (returns redirect URL)
  public static getSummaryPdf(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.get(`${baseUrl(id_company)}/payroll-runs/${id_run}/summary/pdf`, authConfig()));
  }

  // Download payslip PDF (returns redirect URL)
  public static getPayslipPdf(
    id_company: string,
    id_payslip: string
  ): Promise<ApiResponse> {
    return request(() => axios.get(`${baseUrl(id_company)}/payslips/${id_payslip}/pdf`, authConfig()));
  }

  // Regenerate PDFs for a validated run
  public static regeneratePdfs(
    id_company: string,
    id_run: string
  ): Promise<ApiResponse> {
    return request(() => axios.post(`${baseUrl(id_company)}/payroll-runs/${id_run}/generate-pdfs`, {}, authConfig()));
  }
}
